import { useMutation, useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { isAdminRole, type Role } from '../api.ts';
import { t } from '../i18n/index.ts';
import {
  EvidenceReasonRequired,
  getAssetContent,
  isPreviewable,
  listAssets,
  previewKind,
  type EvidenceAsset,
  type ReasonChallenge,
} from '../lib/evidence.ts';
import {
  createRedaction,
  type RedactionRegionInput,
  type RedactionResult,
} from '../lib/evidence-workspace.ts';
import {
  Button,
  Empty,
  Field,
  Finding,
  Label,
  Mono,
  Section,
  Select,
  Spinner,
  Status,
} from '../ui.tsx';
import { EvidenceGate, formatWhen, shortId } from './evidence-gate.tsx';

/**
 * Redaction — create a redaction derivative of a committed asset.
 *
 * The demo runs on the NullAdapter (`evac-evidence-service`
 * `src/redaction/null-adapter.ts`): it records the regions the operator names
 * as a signed derivative *declaration* and burns in NO pixels. The service says
 * so on the response and this screen surfaces that verbatim — a derivative is
 * never presented as altered media when it is not (court-grade honesty).
 *
 * Naming a box by typing six fractions is how the first build worked, and it is
 * still the keyboard-and-fallback path (a region a screen-reader user can set,
 * and the only path for an asset that cannot be previewed inline). When the
 * asset *can* be previewed, the operator drags the box on the rendered frame
 * instead — the same blob-URL, CSP-safe rendering the Evidence module's
 * `AssetPreview` uses — and the drag writes the same fractions the fields hold.
 */

interface RegionDraft {
  startMs: string;
  endMs: string;
  x: string;
  y: string;
  w: string;
  h: string;
}

function blankRegion(): RegionDraft {
  return { startMs: '0', endMs: '1000', x: '0', y: '0', w: '1', h: '1' };
}

/** A fraction as a short, stable string — what the drag surface writes back. */
function fmtFrac(v: number): string {
  return String(Math.round(v * 10000) / 10000);
}

/** Parse the drafts, or return a human error naming the first problem. */
function parseRegions(
  drafts: RegionDraft[],
): { regions: RedactionRegionInput[] } | { error: string } {
  const regions: RedactionRegionInput[] = [];
  for (let i = 0; i < drafts.length; i++) {
    const d = drafts[i]!;
    const nums = {
      startMs: Number(d.startMs),
      endMs: Number(d.endMs),
      x: Number(d.x),
      y: Number(d.y),
      w: Number(d.w),
      h: Number(d.h),
    };
    const label = `Region ${i + 1}`;
    if (Object.values(nums).some((n) => Number.isNaN(n))) {
      return { error: `${label}: every value must be a number.` };
    }
    if (!Number.isInteger(nums.startMs) || !Number.isInteger(nums.endMs) || nums.startMs < 0) {
      return { error: `${label}: start and end are whole milliseconds, and start is 0 or more.` };
    }
    if (nums.endMs <= nums.startMs) {
      return { error: `${label}: end must be after start.` };
    }
    if (
      [nums.x, nums.y].some((n) => n < 0 || n > 1) ||
      [nums.w, nums.h].some((n) => n <= 0 || n > 1)
    ) {
      return { error: `${label}: box x/y are 0..1 and w/h are between 0 and 1.` };
    }
    if (nums.x + nums.w > 1 || nums.y + nums.h > 1) {
      return { error: `${label}: the box must fit inside the frame (x+w ≤ 1, y+h ≤ 1).` };
    }
    regions.push({
      startMs: nums.startMs,
      endMs: nums.endMs,
      box: { x: nums.x, y: nums.y, w: nums.w, h: nums.h },
    });
  }
  return { regions };
}

export function Redaction({
  orgId,
  role,
  onGoToEvidence,
}: {
  orgId: string;
  role: Role;
  onGoToEvidence?: () => void;
}) {
  const qc = useQueryClient();
  const [challenge, setChallenge] = useState<ReasonChallenge | null>(null);
  const canWrite = isAdminRole(role);

  const assets = useQuery({
    queryKey: ['evidence-assets', orgId],
    queryFn: async () => {
      try {
        return await listAssets(orgId);
      } catch (e) {
        if (e instanceof EvidenceReasonRequired) {
          setChallenge(e.challenge);
          return { items: [], next_cursor: null, total: 0, filtered_total: 0 };
        }
        throw e;
      }
    },
    retry: false,
  });

  const onStated = () => {
    setChallenge(null);
    void qc.invalidateQueries({ queryKey: ['evidence-assets', orgId] });
  };

  const stored = (assets.data?.items ?? []).filter((a: EvidenceAsset) => a.state === 'stored');

  return (
    <EvidenceGate
      title={t('redaction.title')}
      sub={t('redaction.sub')}
      err={assets.error}
      loading={assets.isLoading}
      challenge={challenge}
      onStated={onStated}
      onGoToEvidence={onGoToEvidence}
    >
      {!canWrite ? (
        <Empty>{t('redaction.noRole')}</Empty>
      ) : stored.length === 0 ? (
        <Empty>{t('redaction.noEvidence')}</Empty>
      ) : (
        <RedactForm orgId={orgId} assets={stored} />
      )}
    </EvidenceGate>
  );
}

function RedactForm({ orgId, assets }: { orgId: string; assets: EvidenceAsset[] }) {
  const [assetId, setAssetId] = useState('');
  const [regions, setRegions] = useState<RegionDraft[]>([blankRegion()]);
  // The region the drag surface and scrubber act on. The fields below stay
  // editable for every region; this only chooses which one the frame edits.
  const [active, setActive] = useState(0);
  const [error, setError] = useState('');
  const [result, setResult] = useState<RedactionResult | null>(null);

  const asset = assets.find((a) => a.id === assetId);
  const kind = asset ? previewKind(asset.media_type) : null;
  const canPreview = asset ? isPreviewable(asset) && kind !== null : false;

  const redact = useMutation({
    mutationFn: (input: RedactionRegionInput[]) => createRedaction(orgId, assetId, input),
    onSuccess: (r) => {
      setResult(r);
      setError('');
    },
    onError: (e: Error) => {
      setResult(null);
      setError(e.message);
    },
  });

  const setField = (i: number, key: keyof RegionDraft, value: string) => {
    setRegions((prev) => prev.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  };

  const setBox = (i: number, box: { x: number; y: number; w: number; h: number }) => {
    setRegions((prev) =>
      prev.map((r, j) =>
        j === i
          ? { ...r, x: fmtFrac(box.x), y: fmtFrac(box.y), w: fmtFrac(box.w), h: fmtFrac(box.h) }
          : r,
      ),
    );
  };

  const setSpan = (i: number, startMs: number, endMs: number) => {
    setRegions((prev) =>
      prev.map((r, j) => (j === i ? { ...r, startMs: String(startMs), endMs: String(endMs) } : r)),
    );
  };

  const addRegion = () => {
    setRegions((prev) => [...prev, blankRegion()]);
    setActive(regions.length);
  };

  const removeRegion = (i: number) => {
    setRegions((prev) => prev.filter((_, j) => j !== i));
    setActive((a) => (a >= i && a > 0 ? a - 1 : a));
  };

  const submit = () => {
    if (!assetId) {
      setError('Choose an asset to redact.');
      return;
    }
    const parsed = parseRegions(regions);
    if ('error' in parsed) {
      setError(parsed.error);
      return;
    }
    redact.mutate(parsed.regions);
  };

  const activeRegion = regions[active] ?? regions[0]!;

  return (
    <>
      <Section title={t('redaction.form.title')}>
        <Finding tone="attention">
          The demo runs on the NullAdapter: it records the regions you name as a signed derivative
          declaration and alters no pixels. Nothing is burned in until a real redaction engine is
          fitted.
        </Finding>
        {error ? <Finding tone="alert">{error}</Finding> : null}

        <div className="mb-4 max-w-[560px]">
          <Select
            label={t('common.committedEvidence')}
            value={assetId}
            onChange={(v) => {
              setAssetId(v);
              setActive(0);
              setResult(null);
            }}
            options={[
              { value: '', label: t('common.chooseAsset') },
              ...assets.map((a) => ({
                value: a.id,
                label: `${a.media_type} · ${a.merkle_root.slice(0, 12)}… · ${formatWhen(a.captured_at)}`,
              })),
            ]}
          />
        </div>

        {asset && canPreview ? (
          <div className="mb-6">
            <div className="mb-1.5">
              <Label>Mark region {active + 1} on the frame</Label>
            </div>
            <p className="mb-3 max-w-prose text-[13px] text-[var(--ink-2)]">
              Drag on the frame to draw the box for region {active + 1}, and — on a recording — set
              its time span below. The values write straight into the fields further down, so you
              can also type them by hand. This is a rendering drawn from an in-memory copy for
              marking; it changes nothing, and the declaration records the regions, not altered
              pixels.
            </p>
            <RegionCanvas
              orgId={orgId}
              asset={asset}
              kind={kind!}
              region={activeRegion}
              onBox={(box) => setBox(active, box)}
              onSpan={(s, e) => setSpan(active, s, e)}
            />
          </div>
        ) : asset && !canPreview ? (
          <Finding tone="neutral">
            This asset can’t be previewed inline (its type or size is past the inline limit), so
            mark its regions by number below. The original stays the authoritative artefact either
            way.
          </Finding>
        ) : null}

        <div className="mb-2">
          <Label>{t('redaction.regions.label')}</Label>
        </div>
        <p className="mb-3 max-w-prose text-[13px] text-[var(--ink-2)]">
          Each region names a time span in milliseconds and a box as fractions of the frame (0 to 1,
          top-left origin). At least one region is required.
        </p>

        <div className="flex flex-col gap-4">
          {regions.map((r, i) => (
            <fieldset
              key={i}
              className={`border p-3 ${
                canPreview && i === active ? 'border-[var(--focus)]' : 'border-[var(--line)]'
              }`}
            >
              <legend className="px-1">
                <Label>Region {i + 1}</Label>
              </legend>
              {canPreview ? (
                <div className="mb-3">
                  {i === active ? (
                    <Status tone="attention">Marking on the frame</Status>
                  ) : (
                    <Button
                      variant="quiet"
                      label={`Mark region ${i + 1} on the frame`}
                      onClick={() => setActive(i)}
                    >
                      Mark on frame
                    </Button>
                  )}
                </div>
              ) : null}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Field
                  label="Start (ms)"
                  value={r.startMs}
                  onChange={(v) => setField(i, 'startMs', v)}
                  inputMode="numeric"
                />
                <Field
                  label="End (ms)"
                  value={r.endMs}
                  onChange={(v) => setField(i, 'endMs', v)}
                  inputMode="numeric"
                />
                <div className="hidden sm:block" />
                <Field
                  label="Box x"
                  value={r.x}
                  onChange={(v) => setField(i, 'x', v)}
                  inputMode="numeric"
                />
                <Field
                  label="Box y"
                  value={r.y}
                  onChange={(v) => setField(i, 'y', v)}
                  inputMode="numeric"
                />
                <div className="hidden sm:block" />
                <Field
                  label="Box w"
                  value={r.w}
                  onChange={(v) => setField(i, 'w', v)}
                  inputMode="numeric"
                />
                <Field
                  label="Box h"
                  value={r.h}
                  onChange={(v) => setField(i, 'h', v)}
                  inputMode="numeric"
                />
              </div>
              {regions.length > 1 ? (
                <div className="mt-3">
                  <Button
                    variant="quiet"
                    label={`Remove region ${i + 1}`}
                    onClick={() => removeRegion(i)}
                  >
                    {t('redaction.regions.removeRegion')}
                  </Button>
                </div>
              ) : null}
            </fieldset>
          ))}
        </div>

        <div className="mt-4 flex gap-2">
          <Button variant="secondary" onClick={addRegion}>
            {t('redaction.regions.addRegion')}
          </Button>
          <Button variant="primary" disabled={redact.isPending} onClick={submit}>
            {redact.isPending ? t('redaction.creating') : t('redaction.create')}
          </Button>
        </div>
      </Section>

      {result ? <RedactionResultView result={result} /> : null}
    </>
  );
}

/**
 * The visual box-and-span picker over a committed asset.
 *
 * The bytes come from `getAssetContent` and are drawn from an in-memory `blob:`
 * URL — the same CSP-safe path `AssetPreview` uses, so nothing is loaded from an
 * external host. A pointer drag on the frame derives the box as fractions of the
 * frame (0..1, top-left origin); for a recording a start/end scrubber sets the
 * time span, bounded by the media's own duration.
 *
 * Pointer is an enhancement, never the only way in: the numeric fields the
 * parent renders remain the keyboard-and-screen-reader path, and this surface
 * writes into them rather than replacing them.
 */
function RegionCanvas({
  orgId,
  asset,
  kind,
  region,
  onBox,
  onSpan,
}: {
  orgId: string;
  asset: EvidenceAsset;
  kind: 'image' | 'video';
  region: RegionDraft;
  onBox: (box: { x: number; y: number; w: number; h: number }) => void;
  onSpan: (startMs: number, endMs: number) => void;
}) {
  const content = useQuery({
    queryKey: ['evidence-content', orgId, asset.id],
    queryFn: () => getAssetContent(orgId, asset.id),
    retry: false,
    staleTime: Infinity,
  });

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    const blob = content.data;
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setObjectUrl(null);
    };
  }, [content.data]);

  const frameRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<{ x: number; y: number } | null>(null);
  const [drawing, setDrawing] = useState(false);
  // A video carries its own controls, so drawing is a mode you turn on; an image
  // has nothing else to interact with, so it draws straight away.
  const [drawMode, setDrawMode] = useState(kind === 'image');
  const [durationMs, setDurationMs] = useState<number | null>(null);

  const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

  const fracAt = (clientX: number, clientY: number) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    return {
      x: clamp01((clientX - rect.left) / rect.width),
      y: clamp01((clientY - rect.top) / rect.height),
    };
  };

  const onPointerDown = (e: ReactPointerEvent) => {
    const p = fracAt(e.clientX, e.clientY);
    if (!p) return;
    anchorRef.current = p;
    setDrawing(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!drawing || !anchorRef.current) return;
    const p = fracAt(e.clientX, e.clientY);
    if (!p) return;
    const a = anchorRef.current;
    onBox({
      x: Math.min(a.x, p.x),
      y: Math.min(a.y, p.y),
      w: Math.abs(p.x - a.x),
      h: Math.abs(p.y - a.y),
    });
  };

  const endDrag = (e: ReactPointerEvent) => {
    if (!drawing) return;
    setDrawing(false);
    anchorRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // capture may already be gone; nothing to release.
    }
  };

  // The current box, for the outline. Invalid/empty draws nothing rather than a
  // zero-size artefact.
  const bx = Number(region.x);
  const by = Number(region.y);
  const bw = Number(region.w);
  const bh = Number(region.h);
  const hasBox = [bx, by, bw, bh].every(Number.isFinite) && bw > 0 && bh > 0;

  const mediaClass = 'block max-h-[440px] w-auto max-w-full';

  let frame: ReactNode;
  if (content.isLoading) {
    frame = <Spinner>Loading the frame</Spinner>;
  } else if (content.error || !objectUrl) {
    frame = (
      <Finding tone="neutral">
        The frame couldn’t be drawn here. Nothing is lost — mark the region by number below, and the
        original is untouched.
      </Finding>
    );
  } else {
    frame = (
      <div
        ref={frameRef}
        className="relative inline-block max-w-full touch-none border border-[var(--line)] bg-black align-top select-none"
      >
        {kind === 'image' ? (
          <img
            src={objectUrl}
            alt="Committed evidence, for marking redaction regions"
            className={mediaClass}
          />
        ) : (
          <video
            src={objectUrl}
            controls={!drawMode}
            aria-label="Committed evidence, for marking redaction regions"
            className={mediaClass}
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              if (Number.isFinite(d) && d > 0) setDurationMs(Math.round(d * 1000));
            }}
          />
        )}
        {hasBox ? (
          <div
            aria-hidden
            className="pointer-events-none absolute border-2 border-[var(--accent)] bg-[var(--accent)]/15"
            style={{
              left: `${bx * 100}%`,
              top: `${by * 100}%`,
              width: `${bw * 100}%`,
              height: `${bh * 100}%`,
            }}
          />
        ) : null}
        {drawMode ? (
          <div
            aria-hidden
            className="absolute inset-0 cursor-crosshair"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {frame}

      {kind === 'video' && objectUrl ? (
        <Button
          variant="secondary"
          onClick={() => setDrawMode((m) => !m)}
          label={drawMode ? 'Stop drawing and use the player' : 'Draw a redaction box on the frame'}
        >
          {drawMode ? 'Done drawing' : 'Draw box'}
        </Button>
      ) : null}

      {kind === 'video' ? (
        durationMs != null ? (
          <SpanScrubber region={region} durationMs={durationMs} onSpan={onSpan} />
        ) : (
          <p className="text-[13px] text-[var(--ink-3)]">
            Play the recording to load its length, then set the time span here or in the fields
            below.
          </p>
        )
      ) : null}
    </div>
  );
}

/** Start/end sliders for a recording's redaction span, bounded by its length. */
function SpanScrubber({
  region,
  durationMs,
  onSpan,
}: {
  region: RegionDraft;
  durationMs: number;
  onSpan: (startMs: number, endMs: number) => void;
}) {
  const start = Math.min(Math.max(0, Number(region.startMs) || 0), durationMs);
  const end = Math.min(Math.max(0, Number(region.endMs) || 0), durationMs);
  const secs = (ms: number) => `${(ms / 1000).toFixed(1)}s`;

  return (
    <div className="flex max-w-[440px] flex-col gap-3">
      <label className="block">
        <span className="mb-1 flex items-baseline justify-between">
          <Label>Span start</Label>
          <span className="tabular text-[13px] text-[var(--ink-2)]">{secs(start)}</span>
        </span>
        <input
          type="range"
          min={0}
          max={durationMs}
          step={100}
          value={start}
          onChange={(e) => {
            const s = Number(e.target.value);
            onSpan(s, Math.max(end, s + 100));
          }}
          className="w-full accent-[var(--accent)]"
        />
      </label>
      <label className="block">
        <span className="mb-1 flex items-baseline justify-between">
          <Label>Span end</Label>
          <span className="tabular text-[13px] text-[var(--ink-2)]">{secs(end)}</span>
        </span>
        <input
          type="range"
          min={0}
          max={durationMs}
          step={100}
          value={end}
          onChange={(e) => {
            const eMs = Number(e.target.value);
            onSpan(Math.min(start, eMs - 100), eMs);
          }}
          className="w-full accent-[var(--accent)]"
        />
      </label>
    </div>
  );
}

function RedactionResultView({ result }: { result: RedactionResult }) {
  return (
    <Section title={t('redaction.result.title')}>
      {/* The service's own honest note, verbatim. Present only when nothing was
          burned in, which is exactly what must not be hidden. */}
      {result.burned_in === false ? (
        <Finding tone="attention">
          {result.note ??
            'regions declared, not yet burned-in — no pixels altered (demo NullAdapter)'}
        </Finding>
      ) : (
        <Finding tone="safe">Pixels were burned in by {result.engine}.</Finding>
      )}
      <div className="grid max-w-[720px] grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-[14px]">
        <Label>Derivative id</Label>
        <Mono>{result.derivative_asset_id}</Mono>
        <Label>Parent asset</Label>
        <Mono>{shortId(result.parent_asset_id, 12)}</Mono>
        <Label>Method</Label>
        <span>{result.method}</span>
        <Label>Burned in</Label>
        <span>{result.burned_in ? 'Yes' : 'No — no pixels altered'}</span>
        <Label>Engine</Label>
        <span>
          {result.engine} v{result.engine_version}
          {result.model_version ? ` · model ${result.model_version}` : ' · no detection ran'}
        </span>
        <Label>Regions</Label>
        <span className="tabular">{result.region_count}</span>
        <Label>Region-set hash</Label>
        <Mono>{result.region_set_hash}</Mono>
        <Label>Content hash</Label>
        <Mono>{result.merkle_root}</Mono>
        <Label>Manifest hash</Label>
        <Mono>{result.manifest_hash}</Mono>
      </div>
    </Section>
  );
}
